// Shared map utility functions used by MapView and EngagementMap.

const TIGER_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2021/MapServer';
const COUNTY_TIGER_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer';
const TRACT_LAYER_CANDIDATES = [10, 6, 14, 4, 12];
const COUNTY_LAYER_CANDIDATES = [1, 12, 0, 13];
const TIGER_TTL = 7 * 24 * 60 * 60 * 1000; // TIGER boundaries are essentially static

export function getGEOID(props) {
  if (props.GEOID) return props.GEOID;
  if (props.GEOID20) return props.GEOID20;
  if (props.GEOID10) return props.GEOID10;
  const s = props.STATE || props.STATEFP || props.STATEFP20 || '';
  const c = props.COUNTY || props.COUNTYFP || props.COUNTYFP20 || '';
  const t = props.TRACT || props.TRACTCE || props.TRACTCE20 || '';
  if (s && c && t) return `${s.padStart(2, '0')}${c.padStart(3, '0')}${t.padStart(6, '0')}`;
  if (s && c) return `${s.padStart(2, '0')}${c.padStart(3, '0')}`;
  return null;
}

export function getTractName(props) {
  return props.NAMELSAD || props.NAMELSAD20 || props.NAME || props.BASENAME || null;
}

export async function fetchTractGeoJSON(state, county) {
  const cacheKey = `fcp_v1_tiger_tract_${state}_${county}`;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const { d, t } = JSON.parse(raw);
      if (Date.now() - t < TIGER_TTL) return d;
      localStorage.removeItem(cacheKey);
    }
  } catch (_) {}

  const params = new URLSearchParams({
    where: `STATE='${state}' AND COUNTY='${county}'`,
    outFields: '*',
    f: 'geojson',
    outSR: '4326'
  });

  for (const layer of TRACT_LAYER_CANDIDATES) {
    try {
      const res = await fetch(`${TIGER_BASE}/${layer}/query?${params}`);
      if (!res.ok) continue;
      const geojson = await res.json();
      if (geojson.error || !geojson.features?.length) continue;
      const geoid = getGEOID(geojson.features[0]?.properties);
      if (geoid?.length === 11) {
        try { localStorage.setItem(cacheKey, JSON.stringify({ d: geojson, t: Date.now() })); } catch (_) {}
        return geojson;
      }
    } catch (_) {}
  }
  throw new Error('Could not find census tract layer — check state/county FIPS in config');
}

export async function fetchCountyGeoJSON(state) {
  const cacheKey = `fcp_v1_tiger_county_${state}`;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const { d, t } = JSON.parse(raw);
      if (Date.now() - t < TIGER_TTL) return d;
      localStorage.removeItem(cacheKey);
    }
  } catch (_) {}

  const params = new URLSearchParams({
    where: `STATE='${state}'`,
    outFields: '*',
    f: 'geojson',
    outSR: '4326'
  });

  for (const layer of COUNTY_LAYER_CANDIDATES) {
    try {
      const res = await fetch(`${COUNTY_TIGER_BASE}/${layer}/query?${params}`);
      if (!res.ok) continue;
      const geojson = await res.json();
      if (geojson.error || !geojson.features?.length) continue;
      const geoid = getGEOID(geojson.features[0]?.properties);
      if (geoid?.length === 5) {
        try { localStorage.setItem(cacheKey, JSON.stringify({ d: geojson, t: Date.now() })); } catch (_) {}
        return geojson;
      }
    } catch (_) {}
  }
  throw new Error('Could not find county boundary layer — check state FIPS in config');
}

export function augmentFeatures(geojson, geography) {
  const lookup = new Map((geography ?? []).map(g => [g.geoid, g.value]));
  // Detect county-level data: GEOIDs are 5 digits (state+county FIPS)
  const isCountyLevel = (geography ?? []).some(g => g.geoid?.length === 5);

  return {
    ...geojson,
    features: geojson.features.map(f => {
      const geoid = getGEOID(f.properties);
      let value = null;
      if (geoid != null) {
        if (lookup.has(geoid)) {
          value = lookup.get(geoid);
        } else if (isCountyLevel && geoid.length === 11) {
          value = lookup.get(geoid.substring(0, 5)) ?? null;
        }
      }
      return {
        ...f,
        properties: { ...f.properties, _geoid: geoid, data_value: value }
      };
    })
  };
}

export function buildColorExpression(minVal, maxVal, gradient) {
  return [
    'case',
    ['==', ['get', 'data_value'], null],
    '#cccccc',
    ['interpolate', ['linear'], ['get', 'data_value'],
      minVal, gradient.light,
      maxVal, gradient.dark
    ]
  ];
}

export function formatValue(value, unit) {
  if (value === null || value === undefined) return 'N/A';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'dollars') return `$${Math.round(value).toLocaleString()}`;
  if (unit === 'index') return value.toFixed(2);
  return value.toLocaleString();
}

// Compute bounding box from a GeoJSON FeatureCollection
export function getGeoJSONBounds(geojson) {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  function walk(coords) {
    if (typeof coords[0] === 'number') {
      if (coords[0] < minLng) minLng = coords[0];
      if (coords[0] > maxLng) maxLng = coords[0];
      if (coords[1] < minLat) minLat = coords[1];
      if (coords[1] > maxLat) maxLat = coords[1];
    } else {
      coords.forEach(walk);
    }
  }
  geojson.features.forEach(f => { if (f.geometry?.coordinates) walk(f.geometry.coordinates); });
  return isFinite(minLng) ? [[minLng, minLat], [maxLng, maxLat]] : null;
}
